import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Category } from '@/__generated_graphql__/graphql';
import { CategoriesFilter } from '@/components/CategoriesFilter/CategoriesFilter';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { CrossIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { useCategoryModal } from '@/hooks/useCategoryModal';

import * as S from './CategoryModal.styles';

type Props = {
  onClose: () => void;
  value: Category[];
  onChange: (category: Category[]) => void;
  isOpen?: boolean;
  onConfirmCallback?: (categories: Category[]) => void;
};

export const CategoryModal: FC<Props> = ({
  onClose,
  value,
  onChange,
  isOpen = true,
  onConfirmCallback
}) => {
  const { t } = useTranslation();

  const { methods, handleClose, handleConfirm, handleReset, id } =
    useCategoryModal({
      value,
      onChange,
      onClose
    });

  const onSubmit = (formData: { categories: Category[] }) => {
    handleConfirm(formData);
    onConfirmCallback?.(formData?.categories);
  };

  return (
    <Modal
      data-testid="category-modal"
      onClose={handleClose}
      position="center"
      isOpen={isOpen}
    >
      <S.Wrapper>
        <S.HeaderWrapper>
          <Typography variant="h5">{t('CategoryModal.header')}</Typography>
          <S.CrossButton onClick={handleClose} aria-label="close-button">
            <CrossIcon />
          </S.CrossButton>
        </S.HeaderWrapper>

        <S.ContentWrapper>
          <FormProvider {...methods}>
            <form id={id} onSubmit={methods.handleSubmit(onSubmit)}>
              <CategoriesFilter categoryLabelVariant="h5" />
            </form>
          </FormProvider>
        </S.ContentWrapper>

        <S.FooterWrapper>
          <Button variant="secondary" onClick={handleReset}>
            {t('CategoryModal.reset_button')}
          </Button>

          <Button variant="brand" form={id} type="submit" fullWidth>
            {t('CategoryModal.confirm_button')}
          </Button>
        </S.FooterWrapper>
      </S.Wrapper>
    </Modal>
  );
};
